import React, { useState, useEffect, useCallback } from "react";
import {
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Autocomplete,
	DialogContentText,
	Typography,
	CircularProgress,
} from "@mui/material";
import { getLDAPUserByEmpId } from "../Api/Getters";
import isAuthenticated from "./isAuthenticated";

export default function FacultyRankAutocomplete(props) {
	const {
		options: rawOptions,
		currentRow,
		setExistingRoles,
		setNewRoleValue,
		checkExistingFacultyRank,
		addInstructorToFacultyRank,
		isInInstance,
		isInEditClasses,
		setNewlyAddedToFacultyRank,
		size,
	} = props;

	const auth = isAuthenticated();
	const token = auth.token;

	const [options, setOptions] = useState([]);
	const [value, setValue] = useState(null);
	const [inputValue, setInputValue] = useState("");
	const [openSearch, setOpenSearch] = useState(false);

	const [openDialog, setOpenDialog] = useState(false);
	const [dialogValue, setDialogValue] = useState({
		firstName: "",
		lastName: "",
		empid: "",
		ccid: "",
		role: "",
	});

	const [searchedLDAP, setSearchedLDAP] = useState(false);
	const [checked, setChecked] = useState(false);
	const [readyToAdd, setReadyToAdd] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	// Build options and existing roles whenever options or current row change
	useEffect(() => {
		if (!rawOptions || rawOptions.length === 0 || !currentRow) return;

		const roles = currentRow.roles || [];

		const baseOptions = rawOptions.map((element, index) => ({
			key: index,
			empid: element.empid,
			name: element.name,
			ccid: element.ccid,
			rank: element.rank,
		}));

		const existingRolesList = [];
		const remainingOptions = [];

		baseOptions.forEach((opt) => {
			const match = roles.find((role) => role.empid === opt.empid);
			if (match) {
				existingRolesList.push({
					...opt,
					role: match.class_role,
				});
			} else {
				remainingOptions.push(opt);
			}
		});

		if (existingRolesList.length > 0) {
			setExistingRoles(existingRolesList);
		}

		setOptions(remainingOptions);
	}, [rawOptions, currentRow, setExistingRoles]);

	const resetDialogState = useCallback(() => {
		setSearchedLDAP(false);
		setChecked(false);
		setReadyToAdd(false);
		setErrorMessage("");
		setSuccessMessage("");
		setDialogValue({
			firstName: "",
			lastName: "",
			empid: "",
			ccid: "",
			role: "",
		});
	}, []);

	const handleCloseDialog = () => {
		resetDialogState();
		setOpenDialog(false);
	};

	const handleChangeAddInstructor = (fieldName) => (event) => {
		const value = event.target.value;
		setReadyToAdd(false);
		setChecked(false);
		setErrorMessage("");
		setSuccessMessage("");

		if (fieldName === "empid") {
			setSearchedLDAP(false);
		}

		setDialogValue((prev) => ({
			...prev,
			[fieldName]: value,
		}));
	};

	const handleClearInputs = () => {
		setInputValue("");
		setValue(null);
		setOpenSearch(false);
		setNewRoleValue(null);
	};

	const handleAutocompleteChange = (event, newValue) => {
		// "Free solo" / "add new instructor" paths

		if (typeof newValue === "string") {
			setOpenSearch(false);
			resetDialogState();
			setOpenDialog(true);
			// Start with empty empid; they must enter one
			setDialogValue((prev) => ({
				...prev,
				empid: "",
			}));
			return;
		}

		if (newValue && newValue.inputValue) {
			setOpenSearch(false);
			resetDialogState();
			setOpenDialog(true);
			setDialogValue((prev) => ({
				...prev,
				empid: "",
			}));
			return;
		}

		// Normal selection
		setValue(newValue);
		setNewRoleValue(newValue);
		setOpenSearch(false);
	};

	const handleAutocompleteInputChange = (event, newInputValue, reason) => {
		// When user clears using the clear button / ESC
		if (reason === "clear") {
			handleClearInputs();
			return;
		}

		// When value is programmatically set (e.g., after selecting an option
		// or when you call setValue yourself), MUI fires onInputChange with reason "reset".
		// Here we want the popup CLOSED and just show the label.
		if (reason === "reset") {
			setInputValue(newInputValue || "");
			setOpenSearch(false);
			return;
		}

		// Normal user typing
		setInputValue(newInputValue);
		setOpenSearch(newInputValue.length > 2);
	};

	const filterOptions = (options, params) => {
		const searchTerm = params.inputValue.toLowerCase().trim();

		if (!searchTerm) {
			return options;
		}

		const searchWords = searchTerm.split(/\s+/);

		const updatedSearchWords = searchWords.filter((word) => word !== "-");

		//remove any commas from search words
		const cleanedSearchWords = updatedSearchWords.map((word) => word.replace(/,/g, ""));

		const filtered = options.filter((option) => {
			const baseText = `${option.name} ${option.empid} ${option.ccid}`.toLowerCase();

			// All search words must be present somewhere in the string
			const allWordsMatch = cleanedSearchWords.every((word) => baseText.includes(word));
			if (allWordsMatch) return true;

			// Try reversed name format if "Last, First"
			if (option.name && option.name.includes(",")) {
				const [lastName, firstName] = option.name.split(",").map((s) => s.trim());
				const reversedName = `${firstName} ${lastName}`.toLowerCase();
				const reversedMatch = cleanedSearchWords.every((word) => reversedName.includes(word));
				if (reversedMatch) return true;
			}

			return false;
		});

		// Add "Add new instructor" option if no matches
		if (searchTerm !== "" && filtered.length === 0) {
			filtered.push({
				inputValue: params.inputValue,
				name: `Add "${params.inputValue}" as a new instructor?`,
			});
		}

		return filtered;
	};

	const getOptionLabel = (option) => {
		if (typeof option === "string") return option;
		if (option.inputValue) return option.inputValue;

		if (option.empid && option.ccid) {
			const paddedEmpid = String(option.empid).padStart(7, "0");
			return `${option.name} - ${paddedEmpid} - ${option.ccid}`;
		}

		return option.name || "";
	};

	const formatEmpid = (empid) => String(empid).padStart(7, "0");

	const renderOption = (props, option) => {
		// "Add new" option
		if (option.inputValue) {
			return (
				<li {...props} className={`${props.className || ""} text-sm font-semibold text-primary`}>
					{option.name}
				</li>
			);
		}

		if (option.empid && option.ccid) {
			const paddedEmpid = formatEmpid(option.empid);
			return (
				<li
					{...props}
					title={`${option.name} - ${option.ccid} - ${paddedEmpid}`}
					className={`${props.className || ""} min-w-0`}
				>
					<div className="flex min-w-0 w-full items-center gap-2 text-xs leading-5">
						<span className="min-w-0 flex-1 truncate font-semibold text-gray-900">
							{option.name}
						</span>
						<span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 font-semibold text-primary">
							{option.ccid}
						</span>
						<span className="shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-700">
							{paddedEmpid}
						</span>
					</div>
				</li>
			);
		}

		return (
			<li {...props} className={`${props.className || ""} text-sm text-gray-900`}>
				{option.name || ""}
			</li>
		);
	};

	const checkExistingAndLDAP = async () => {
		setErrorMessage("");
		setSuccessMessage("");
		setReadyToAdd(false);
		setChecked(false);

		if (!dialogValue.empid) {
			setErrorMessage("Please enter an employee ID.");
			return;
		}

		const alreadyExists = checkExistingFacultyRank(dialogValue.empid);
		if (alreadyExists !== false) {
			setErrorMessage("Faculty member with this employee ID already exists in our database.");
			return;
		}

		try {
			setLoading(true);
			const res = await getLDAPUserByEmpId(token, dialogValue.empid);

			if (
				res &&
				res.first_name &&
				res.last_name &&
				res.ccid &&
				res.first_name.length > 0 &&
				res.last_name.length > 0 &&
				res.ccid.length > 0
			) {
				setSearchedLDAP(true);
				setDialogValue((prev) => ({
					...prev,
					firstName: res.first_name,
					lastName: res.last_name,
					ccid: res.ccid,
				}));
				setReadyToAdd(true);
				setChecked(true);
				setErrorMessage("");
				setSuccessMessage("Ready to add instructor.");
			} else {
				setErrorMessage("Employee ID not found in LDAP.");
			}
		} catch (err) {
			setErrorMessage("There was an error looking up this employee ID.");
		} finally {
			setLoading(false);
		}
	};

	const handleAddInstructorToFacultyRank = async () => {
		setLoading(true);
		setErrorMessage("");
		setSuccessMessage("");

		const newInstructor = {
			name: `${dialogValue.lastName.trim()}, ${dialogValue.firstName.trim()}`,
			empid: dialogValue.empid,
			ccid: dialogValue.ccid,
			rank: "INSTRUCTOR",
		};

		const success = await addInstructorToFacultyRank(newInstructor);

		if (success) {
			if (isInInstance && setNewlyAddedToFacultyRank) {
				setNewlyAddedToFacultyRank(newInstructor);
			}
			if (isInEditClasses) {
				setValue(newInstructor);
				setNewRoleValue(newInstructor);
				setOpenSearch(false);
			}
			setLoading(false);
			handleCloseDialog();
		} else {
			setLoading(false);
			setSuccessMessage("");
		}
	};

	return (
		<>
			<Autocomplete
				value={value}
				size={size ? size : "medium"}
				onChange={handleAutocompleteChange}
				inputValue={inputValue}
				onInputChange={handleAutocompleteInputChange}
				filterOptions={filterOptions}
				options={options}
				getOptionLabel={getOptionLabel}
				renderOption={renderOption}
				filterSelectedOptions
				selectOnFocus
				clearOnBlur
				freeSolo
				id="faculty-rank-autocomplete"
				open={openSearch}
				sx={{ width: "100%", bgcolor: "white" }}
				renderInput={(params) => (
					<div className="relative">
						<TextField {...params} label="Enter Name, Employee ID, or CCID" />
						{/* {inputValue.length > 0 && (
							<button
								type="button"
								onClick={handleClearInputs}
								className="absolute right-4 px-2 top-3 bg-white rounded-full border-2 border-transparent hover:bg-gray-200"
							>
								x
							</button>
						)} */}
					</div>
				)}
			/>

			<Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg">
				<form>
					<DialogTitle>Add New Instructor</DialogTitle>
					<DialogContent
						sx={{
							display: "flex",
							flexDirection: "row",
							flexWrap: "wrap",
							gap: "10px",
						}}
					>
						<DialogContentText>
							NOTE: This form will NOT add this instructor to the course. After adding the
							instructor to our database through this form, you will need to continue on in the
							previous step and add them to the course.
						</DialogContentText>

						<TextField
							required
							margin="dense"
							id="empid"
							value={dialogValue.empid || ""}
							onChange={handleChangeAddInstructor("empid")}
							label="Employee ID"
							variant="standard"
						/>

						{searchedLDAP && (
							<>
								<TextField
									autoFocus
									required
									margin="dense"
									id="lastName"
									value={dialogValue.lastName || ""}
									onChange={handleChangeAddInstructor("lastName")}
									label="Last Name"
									type="text"
									variant="standard"
								/>
								<TextField
									required
									margin="dense"
									id="firstName"
									value={dialogValue.firstName || ""}
									onChange={handleChangeAddInstructor("firstName")}
									label="First Name"
									type="text"
									variant="standard"
								/>
								<TextField
									required
									margin="dense"
									id="ccid"
									disabled
									value={dialogValue.ccid || ""}
									label="CCID"
									type="text"
									variant="standard"
								/>
							</>
						)}
					</DialogContent>

					<DialogActions sx={{ display: "flex", justifyContent: "flex-start", pl: 2 }}>
						{checked ? (
							readyToAdd ? (
								<Button
									disabled={
										!dialogValue.lastName ||
										!dialogValue.firstName ||
										!dialogValue.empid ||
										!dialogValue.ccid ||
										loading
									}
									onClick={handleAddInstructorToFacultyRank}
								>
									{loading ? <CircularProgress size={20} /> : "Add"}
								</Button>
							) : (
								<Button disabled>Add</Button>
							)
						) : (
							<Button
								variant="outlined"
								disabled={!dialogValue.empid || loading}
								onClick={checkExistingAndLDAP}
							>
								Check
							</Button>
						)}

						<Button onClick={handleCloseDialog} disabled={loading}>
							Cancel
						</Button>

						<Typography sx={{ color: "green" }}>{successMessage}</Typography>
						<Typography sx={{ color: "red" }}>{errorMessage}</Typography>
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
}
